// TypeScript types for data connector system

export enum JobStatus {
  PENDING = 'pending',
  QUEUED = 'queued', 
  RUNNING = 'running',
  COMPLETED = 'completed',
  FAILED = 'failed',
  CANCELLED = 'cancelled',
  PAUSED = 'paused'
}

export enum ProcessingMode {
  BATCH = 'batch',
  REALTIME = 'realtime'
}

export enum MetadataStorage {
  SOURCE_TAGS = 'source_tags',
  EXTERNAL_DB = 'external_db',
  SIDECAR_FILES = 'sidecar_files'
}

export type ConnectorType = 's3' | 'mongodb';

export interface ConnectorJob {
  id: string;
  name: string;
  description?: string;
  user_id: string;
  connector_type: ConnectorType;
  source_config: Record<string, any>;
  auth_config: Record<string, any>;
  capability: string;
  processing_mode: ProcessingMode;
  file_filters: string[];
  batch_size: number;
  max_documents?: number;
  metadata_storage: MetadataStorage;
  metadata_config: Record<string, any>;
  schedule?: string;
  enabled: boolean;
  status: JobStatus;
  created_at: string;
  updated_at: string;
  last_run?: string;
  next_run?: string;
}

export interface ConnectorJobRun {
  id: string;
  job_id: string;
  status: JobStatus;
  started_at: string;
  completed_at?: string;
  documents_discovered: number;
  documents_processed: number;
  documents_failed: number;
  error_message?: string;
  metrics: Record<string, any>;
}

export interface ProcessingResult {
  id: string;
  job_run_id: string;
  document_id: string;
  source_path: string;
  analysis_type: string;
  classification?: string;
  confidence?: number;
  reasoning?: string;
  metadata: Record<string, any>;
  processed_at: string;
  processing_time?: number;
  error?: string;
}

export interface ConnectorTemplate {
  id: string;
  name: string;
  description?: string;
  user_id: string;
  connector_type: ConnectorType;
  template_config: Record<string, any>;
  created_at: string;
  is_public: boolean;
  usage_count: number;
}

export interface ConnectorCapability {
  name: string;
  display_name: string;
  description: string;
  domain: string;
  supported_file_types: string[];
  estimated_processing_time: number;
}

export interface JobExecutionRequest {
  job_id: string;
  priority: 'high' | 'normal' | 'low';
  override_config?: Record<string, any>;
}

export interface JobExecutionResponse {
  success: boolean;
  job_id: string;
  run_id?: string;
  task_id?: string;
  message: string;
  estimated_completion?: string;
}

export interface JobStatusResponse {
  job_id: string;
  current_status: JobStatus;
  current_run?: ConnectorJobRun;
  progress: Record<string, any>;
  last_update: string;
}

export interface JobListResponse {
  jobs: ConnectorJob[];
  total: number;
  page: number;
  page_size: number;
  has_more: boolean;
}

export interface ConnectionTestResult {
  job_id: string;
  test_result: {
    success: boolean;
    message: string;
    details: Record<string, any>;
  };
  tested_at: string;
}

export interface JobResultsResponse {
  job_id: string;
  run_id: string;
  results: ProcessingResult[];
  total: number;
  offset: number;
  limit: number;
  summary: {
    total: number;
    successful: number;
    failed: number;
    success_rate: number;
  };
}

export interface ConnectorStatistics {
  total_jobs: number;
  status_distribution: Record<string, number>;
  connector_distribution: Record<string, number>;
  recent_jobs: number;
  templates_available: number;
}

// API request types
export interface ListConnectorsRequest {
  status?: JobStatus;
  connectorType?: ConnectorType;
  page?: number;
  pageSize?: number;
}

export interface CreateConnectorRequest extends Omit<ConnectorJob, 'id' | 'user_id' | 'created_at' | 'updated_at'> {}

export interface UpdateConnectorRequest extends Partial<Omit<ConnectorJob, 'id' | 'user_id' | 'created_at'>> {}